import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signInWithPopup, 
  GoogleAuthProvider, 
  sendEmailVerification 
} from 'firebase/auth';

const checkUserProfile = async (user) => {
  try {
    const token = await user.getIdToken();
    const response = await fetch('/api/userProfile', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (response.ok) {
      const data = await response.json();
      const isComplete = Boolean(
        data.firstName && 
        data.lastName && 
        data.dateOfBirth &&
        data.phoneNumber &&
        data.email &&
        data.billingAddress?.street &&
        data.billingAddress?.city &&
        data.billingAddress?.postalCode &&
        data.billingAddress?.country
      );

      return isComplete ? '/' : '/user-details';
    } else {
      return '/user-details';
    }
  } catch (error) {
    console.error('Chyba při kontrole profilu:', error);
    return '/user-details';
  }
};

const Auth = () => {
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (isLoginMode) {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        if (!userCredential.user.emailVerified) {
          setError('Prosím ověřte svůj email před přihlášením');
          return;
        }
        const redirectPath = await checkUserProfile(userCredential.user);
        navigate(redirectPath);
      } else {
        if (password !== confirmPassword) {
          setError('Hesla se neshodují');
          return;
        }
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        await sendEmailVerification(userCredential.user);
        setVerificationSent(true);
      }
    } catch (error) {
      console.error('Chyba při odesílání:', error);
      setError(error.message);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const redirectPath = await checkUserProfile(result.user);
      navigate(redirectPath);
    } catch (error) {
      setError(error.message);
    }
  };

  const resendVerificationEmail = async () => {
    try {
      if (auth.currentUser) {
        await sendEmailVerification(auth.currentUser);
        setVerificationSent(true);
      }
    } catch (error) {
      console.error('Chyba při opětovném odesílání:', error);
      setError(error.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-md">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {isLoginMode ? 'Přihlášení' : 'Registrace'}
          </h2>
        </div>

        <div className="flex justify-center space-x-4 border-b">
          <button
            className={`pb-2 px-4 ${isLoginMode ? 'border-b-2 border-blue-500' : ''}`}
            onClick={() => setIsLoginMode(true)}
          >
            Přihlášení
          </button>
          <button
            className={`pb-2 px-4 ${!isLoginMode ? 'border-b-2 border-blue-500' : ''}`}
            onClick={() => setIsLoginMode(false)}
          >
            Registrace
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none rounded relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Heslo
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none rounded relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
              />
            </div>
            {!isLoginMode && (
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Potvrzení hesla
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="appearance-none rounded relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                />
              </div>
            )}
          </div>

          <div>
            <button
              type="submit"
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {isLoginMode ? 'Přihlásit se' : 'Zaregistrovat se'}
            </button>
          </div>
        </form>

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Nebo pokračujte s</span>
            </div>
          </div>

          <div className="mt-6">
            <button
              onClick={handleGoogleSignIn}
              className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <img
                className="h-5 w-5 mr-2"
                src="https://www.google.com/favicon.ico"
                alt="Google logo"
              />
              Google
            </button>
          </div>
        </div>

        {verificationSent && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
            Ověřovací email byl odeslán. Prosím zkontrolujte svou emailovou schránku.
          </div>
        )}

        {!isLoginMode && (
          <button
            type="button"
            onClick={resendVerificationEmail}
            className="mt-2 w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
          >
            Znovu odeslat ověřovací email
          </button>
        )}
      </div>
    </div>
  );
};

export default Auth; 